import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { ActivityService, YearAndMonth } from "../../services/activity.service";
import { WorkplaceService } from "../../services/workplace.service";
import { WorkgroupService } from "../../services/workgroup.service";
import { OverviewService } from "./overview.service";
import { Observable } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { combineLatest } from "rxjs/observable/combineLatest";
import { of } from "rxjs/observable/of";

@Component({
    selector: "overview-options",
    template: require('./overview-options.component.html'),
    styles: [require('./overview-options.component.css')]
})

export class OverviewOptionsComponent implements OnInit {

    constructor(
        private overviewService: OverviewService,
        private activityService: ActivityService,
        private workplaceService: WorkplaceService,
        private workGroupService: WorkgroupService
    ) { }

    @Input() workplaceId: number;
    @Input() workGroupId: number;
    @Input() year: number;
    @Input() month: number;

    @Output() onNewRemark = new EventEmitter();
    @Output() onClearActivities = new EventEmitter();

    newRemarkClicked(): void {
        this.onNewRemark.emit();
    }

    clearActivitiesClicked(): void {
        this.onClearActivities.emit();
    }

    workplaceIdObservable: Observable<number>;

    // We maintain en extra work group id because internally we must map a falsy workGroupId
    // to -1 to match the default option in the dropdown.
    internalWorkGroupId: number;

    workplaces: Observable<Nema.Crew.Model.Workplace[]>;

    relevantWorkGroups: Observable<Nema.Crew.Model.WorkGroup[]>;

    years: Observable<number[]>;
    months: Month[];

    filterText = '';

    filterTextChanged(): void {
        this.overviewService.changeFilterText(this.filterText);
    }

    onWorkplaceIdChange(workplaceId: number): void {
        this.overviewService.changeWorkplaceId(workplaceId);
        this.internalWorkGroupId = -1;
        this.notifyWorkGroupId(this.internalWorkGroupId);
    }

    onWorkGroupIdChange(workGroupId: number): void {
        this.notifyWorkGroupId(workGroupId);
    }

    private notifyWorkGroupId(workGroupId: number): void {
        this.overviewService.changeWorkGroupId(workGroupId === -1 ? null : workGroupId);
    }

    onYearAndMonthChange(yearAndMonth: YearAndMonth): void {
        this.overviewService.changeYearAndMonth(yearAndMonth);
    }

    onShowBalanceChanged(show: boolean): void {
        this.overviewService.setShowBalance(show);
    }

    onShowCrossWorkplaceBalanceChanged(show: boolean): void {
        this.overviewService.setShowCrossWorkplaceBalance(show);
    }

    ngOnInit(): void {
        this.overviewService.changeIsProcessing(true);
        this.setupWorkplacesAndWorkGroups();
        this.setupYearAndMonth();
    }

    private setupWorkplacesAndWorkGroups(): void {
        this.workplaceIdObservable = this.overviewService.workplaceId;
        this.overviewService.changeWorkplaceId(this.workplaceId);

        this.internalWorkGroupId = !this.workGroupId ? -1 : this.workGroupId;
        this.notifyWorkGroupId(this.internalWorkGroupId);

        this.workplaces = combineLatest(this.workplaceIdObservable, this.workplaceService.getWorkplaces()).pipe(
            take(1),
            map(([currentWorkplaceId, response]) => {
                const workplaces = response.body;

                if (workplaces && workplaces.length && !currentWorkplaceId) {
                    const newWorkplaceId = workplaces[0].Id;
                    this.overviewService.changeWorkplaceId(newWorkplaceId);
                }

                setTimeout(() => {
                    this.overviewService.setOptionsAreInitialized();
                    this.overviewService.changeIsProcessing(false);
                }, 0);

                return workplaces;
            }));

        const allWorkGroups = this.workGroupService.getWorkgroups().pipe(
            map(res => {
                return res.body;
            }));

        this.relevantWorkGroups = combineLatest(this.workplaceIdObservable, allWorkGroups).pipe(
            map(([currentWorkplaceId, workGroups]) => {
                return workGroups.filter(workGroup => !workGroup.WorkplaceId || workGroup.WorkplaceId === currentWorkplaceId)
            }));
    }

    private setupYearAndMonth(): void {
        const d = new Date();
        this.year = this.year ?? d.getFullYear();
        this.month = this.month ?? d.getMonth();
        this.overviewService.changeYearAndMonth({ Year: this.year, Month: this.month });

        this.years = this.workplaceIdObservable.pipe(
            switchMap(currentWorkplaceId => {
                return currentWorkplaceId
                    ? this.activityService.getActivityStartForWorkplace(currentWorkplaceId).pipe(map(response => response.body))
                    : of<YearAndMonth>(null);
            }),
            map(startYearAndMonth => {
                return this.getYears(startYearAndMonth?.Year)
            }));

        this.months = this.getMonths();
    }

    private getYears(startYear: number): number[] {
        startYear = startYear || new Date().getFullYear();
        const years: number[] = [];
        const numberOfYears = new Date().getFullYear() - startYear + 3;
        for (let j = 0; j < numberOfYears; j++) {
            years.push(startYear++);
        }
        return years;
    }

    private getMonths(): Month[] {
        return [
            { id: 0, number: "01", name: "January" },
            { id: 1, number: "02", name: "February" },
            { id: 2, number: "03", name: "March" },
            { id: 3, number: "04", name: "April" },
            { id: 4, number: "05", name: "May" },
            { id: 5, number: "06", name: "June" },
            { id: 6, number: "07", name: "July" },
            { id: 7, number: "08", name: "August" },
            { id: 8, number: "09", name: "September" },
            { id: 9, number: "10", name: "October" },
            { id: 10, number: "11", name: "November" },
            { id: 11, number: "12", name: "December" },
        ];
    }
}

export interface Month {
    id: number;
    number: string;
    name: string;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/activities/overview/overview-options.component.ts